import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { PageHeader } from '../../components/common/PageHeader';
import { Modal } from '../../components/common/Modal';
import { MerchandiseOrder, MerchandiseItem, Role, UserProfile, InvoiceItem } from '../../types';
import { useMerchandiseData } from '../../hooks/useMerchandiseData';
import { getMembersDB, saveInvoiceDB, saveDriveFileDB } from '../../services/db';
import { isSuperAdmin, isUserManagement, hasRolePermission, hasUserRole, formatSellerName } from '../../utils/formatters';
import { getUniformPriceBySize } from '../../utils/pricing';
import { uploadProductImageToDrive, GDRIVE_CONFIG } from '../../lib/gdrive';
import {
  Package,
  ClipboardList,
  ShoppingBag,
  Truck,
  Clock,
  X,
  Check,
  CheckCircle,
  AlertTriangle,
  MessageSquare,
  Filter,
  BarChart3,
  CreditCard,
  ShieldAlert,
  Search,
  Plus,
  Edit,
  Trash2,
  ArrowLeft,
  ChevronRight,
  Info,
  QrCode,
  Eye,
  Copy,
  ExternalLink,
  ShieldCheck,
  FileCheck,
  Upload,
  Image as ImageIcon
} from 'lucide-react';

export const InventoriPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const { orders, invoices, items, isLoading, saveOrder, deleteOrder, saveItem, deleteItem } = useMerchandiseData();

  const [members, setMembers] = useState<UserProfile[]>([]);
  useEffect(() => {
    getMembersDB().then(setMembers).catch(() => { });
  }, []);

  const getInitialView = (): 'orders' | 'inventory' => {
    const path = window.location.pathname;
    if (path.includes('/inventory/stock')) {
      return 'inventory';
    }
    return 'orders';
  };

  const [activeView, setActiveViewState] = useState<'inventory' | 'orders'>(getInitialView);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // CRUD & Detailed View States
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MerchandiseItem | null>(null);
  const [selectedItemForView, setSelectedItemForView] = useState<MerchandiseItem | null>(null);
  const [orderToDelete, setOrderToDelete] = useState<MerchandiseOrder | null>(null);
  const [isDeletingOrder, setIsDeletingOrder] = useState(false);
  const [productToDelete, setProductToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);

  // Form States
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState<'Pakaian' | 'Kelengkapan' | 'Aksesori'>('Pakaian');
  const [formPrice, setFormPrice] = useState<number>(0);
  const [formDescription, setFormDescription] = useState('');
  const [formPhotoUrl, setFormPhotoUrl] = useState('');
  const [formSizes, setFormSizes] = useState<string>('');
  const [formStockStatus, setFormStockStatus] = useState<'Dalam Stok' | 'Stok Rendah' | 'Tiada Stok'>('Dalam Stok');
  const [formStockQuantity, setFormStockQuantity] = useState<number>(0);
  const [formVariations, setFormVariations] = useState<string>('');
  const [viewingProofUrl, setViewingProofUrl] = useState<string | null>(null);
  const [trackingInput, setTrackingInput] = useState<{ [orderId: string]: string }>({});
  const [isSavingTracking, setIsSavingTracking] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Dynamic Specifications Form
  const [formSpecs, setFormSpecs] = useState<{ key: string; value: string }[]>([
    { key: '', value: '' }
  ]);

  // Handle URL updates and popstate sync
  const setActiveView = (view: 'orders' | 'inventory') => {
    setActiveViewState(view);
    const targetPath = view === 'inventory' ? '/inventory/stock' : '/inventory/orders';
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path.includes('/inventory/stock')) {
        setActiveViewState('inventory');
      } else if (path.includes('/inventory/orders') || path === '/inventory') {
        setActiveViewState('orders');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/inventory') {
      window.history.replaceState(null, '', '/inventory/orders');
    }
  }, []);

  // Status update state for orders
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<MerchandiseOrder['status'] | ''>('');

  const isManagement = Boolean(currentUser && isUserManagement(currentUser));
  const canModifyInventory = isManagement;
  const isExecutive = Boolean(currentUser && hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari']));

  // Base managed orders (filtered so that users only see orders for items they own, Superadmin sees all)
  const baseManagedOrders = useMemo(() => {
    if (!currentUser) return [];

    let filtered = orders.filter(o => {
      const item = items.find(i => i.id === o.itemId);
      if (!item) return false;
      if (isSuperAdmin(currentUser)) return true;
      if (item.userId) {
        return item.userId === currentUser.id;
      }
      // Seed/default items without userId belong to Pengerusi (asmm-001)
      return currentUser?.id?.toLowerCase() === 'asmm-001';
    });

    if (!isSuperAdmin(currentUser) && !isExecutive && hasUserRole(currentUser, 'Ketua Gelanggang')) {
      filtered = filtered.filter(o => o.gelanggang === currentUser.gelanggang);
    }
    return filtered;
  }, [orders, items, currentUser]);

  // All orders with active status and search filters
  const managedOrders = useMemo(() => {
    let filtered = baseManagedOrders;
    if (statusFilter !== 'all') {
      filtered = filtered.filter(o => o.status === statusFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(o =>
        o.memberName.toLowerCase().includes(term) ||
        o.itemName.toLowerCase().includes(term) ||
        o.id.toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [baseManagedOrders, statusFilter, searchTerm]);

  // Inventory stats per catalog item (filtered to only show owned items for current user, Superadmin sees all)
  const inventoryStats = useMemo(() => {
    const userItems = items.filter(item => {
      if (isSuperAdmin(currentUser)) return true;
      if (item.userId) {
        return item.userId === currentUser?.id;
      }
      // Seed/default items without userId belong to Pengerusi (asmm-001)
      return currentUser?.id?.toLowerCase() === 'asmm-001';
    });

    return userItems.map(item => {
      const itemOrders = orders.filter(o => o.itemId === item.id);
      const totalOrdered = itemOrders.reduce((acc, o) => acc + o.quantity, 0);
      const pending = itemOrders.filter(o => o.status === 'Menunggu Kelulusan').reduce((a, o) => a + o.quantity, 0);
      const preparing = itemOrders.filter(o => o.status === 'Sedang Disediakan').reduce((a, o) => a + o.quantity, 0);
      const completed = itemOrders.filter(o => o.status === 'Selesai').reduce((a, o) => a + o.quantity, 0);
      const revenue = itemOrders.filter(o => o.status !== 'Dibatalkan').reduce((a, o) => a + o.totalAmount, 0);
      return { ...item, totalOrdered, pending, preparing, completed, revenue, orderCount: itemOrders.length };
    });
  }, [items, orders, currentUser]);

  // Overall stats based on visible/managed orders
  const overallStats = useMemo(() => {
    return {
      totalOrders: baseManagedOrders.length,
      pendingOrders: baseManagedOrders.filter(o => o.status === 'Menunggu Kelulusan').length,
      preparingOrders: baseManagedOrders.filter(o => o.status === 'Sedang Disediakan').length,
      completedOrders: baseManagedOrders.filter(o => o.status === 'Selesai').length,
      totalRevenue: baseManagedOrders.filter(o => o.status !== 'Dibatalkan').reduce((a, o) => a + o.totalAmount, 0)
    };
  }, [baseManagedOrders]);

  const sellerForView = useMemo(() => {
    if (!selectedItemForView) return null;
    const sellerId = selectedItemForView.userId || 'ASMM-001';
    return members.find(m => m.id.toLowerCase() === sellerId.toLowerCase());
  }, [members, selectedItemForView]);

  const getStatusBadgeStyle = (status: MerchandiseOrder['status']) => {
    switch (status) {
      case 'Selesai': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Sedang Disediakan': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Dibatalkan': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-amber-100 text-amber-800 border-amber-200';
    }
  };

  const getInvoicePaymentStatus = (invoiceId?: string) => {
    if (!invoiceId) return 'Tiada Rujukan';
    const match = invoices.find(inv => inv.id === invoiceId);
    return match ? match.status : 'Belum Dibayar';
  };

  const handleUpdateOrderStatus = async (orderId: string, nextStatus: MerchandiseOrder['status']) => {
    const orderToUpdate = orders.find(o => o.id === orderId);
    if (!orderToUpdate) return;

    const isAuthorized = baseManagedOrders.some(o => o.id === orderId);
    if (!isAuthorized) {
      showToast('error', 'Akses Dihalang', 'Anda tidak dibenarkan mengurus tempahan ini.');
      return;
    }

    showToast('info', 'Mengemaskini status tempahan...');
    const updatedOrder: MerchandiseOrder = { ...orderToUpdate, status: nextStatus };

    try {
      await saveOrder(updatedOrder);
      showToast('success', 'Status Dikemaskini', `Tempahan #${orderId.slice(-6)} kini: ${nextStatus}.`);
      setUpdatingOrderId(null);
      setUpdatingStatus('');
    } catch {
      showToast('error', 'Ralat Kemaskini', 'Gagal mengemaskini status tempahan.');
    }
  };

  const handleVerifyQrPayment = async (order: MerchandiseOrder) => {
    showToast('info', 'Mengesahkan bayaran QR...');
    try {
      const updatedOrder: MerchandiseOrder = {
        ...order,
        paymentStatus: 'Lulus / Dibayar',
        status: order.status === 'Menunggu Kelulusan' ? 'Sedang Disediakan' : order.status
      };
      await saveOrder(updatedOrder);

      // Also update linked invoice if exists
      if (order.invoiceId) {
        const inv = invoices.find(i => i.id === order.invoiceId);
        if (inv) {
          await saveInvoiceDB({
            ...inv,
            status: 'Lulus / Dibayar'
          });
        }
      }

      showToast('success', 'Bayaran QR Disahkan!', `Tempahan #${order.id.slice(-6)} telah diluluskan dan status ditukar ke 'Sedang Disediakan'.`);
    } catch {
      showToast('error', 'Ralat', 'Gagal mengesahkan bayaran QR.');
    }
  };

  const handleSaveTracking = async (orderId: string) => {
    const trackingNo = (trackingInput[orderId] || '').trim();
    if (!trackingNo) {
      showToast('error', 'Diperlukan', 'Sila masukkan nombor penjejakan pos.');
      return;
    }
    const orderToUpdate = orders.find(o => o.id === orderId);
    if (!orderToUpdate) return;

    setIsSavingTracking(orderId);
    try {
      await saveOrder({
        ...orderToUpdate,
        trackingNo
      });
      showToast('success', 'No. Penjejakan Disimpan', `Tracking ${trackingNo} berjaya dikemaskini untuk pembeli.`);
    } catch {
      showToast('error', 'Ralat', 'Gagal menyimpan nombor penjejakan.');
    } finally {
      setIsSavingTracking(null);
    }
  };

  // CRUD: Open Add/Edit Modal
  const openAddEditModal = (item: MerchandiseItem | null = null) => {
    if (!canModifyInventory) {
      showToast('error', 'Akses Dihalang', 'Hanya pihak pengurusan sahaja dibenarkan mengurus inventori.');
      return;
    }
    setEditingItem(item);
    if (item) {
      setFormName(item.name);
      setFormCategory(item.category);
      setFormPrice(item.price);
      setFormDescription(item.description);
      setFormPhotoUrl(item.photoUrl);
      setFormSizes(item.sizes ? item.sizes.join(', ') : '');
      setFormStockStatus(item.stockStatus);
      setFormStockQuantity(item.stockQuantity || 0);
      setFormVariations(item.variations ? item.variations.join(', ') : '');

      const specsList = item.specifications
        ? Object.entries(item.specifications).map(([k, v]) => ({ key: k, value: v }))
        : [{ key: '', value: '' }];
      setFormSpecs(specsList.length > 0 ? specsList : [{ key: '', value: '' }]);
    } else {
      setFormName('');
      setFormCategory('Pakaian');
      setFormPrice(0);
      setFormDescription('');
      setFormPhotoUrl('/logo-pssasmm.png');
      setFormSizes('');
      setFormStockStatus('Dalam Stok');
      setFormStockQuantity(50);
      setFormVariations('');
      setFormSpecs([{ key: '', value: '' }]);
    }
    setIsAddEditModalOpen(true);
  };

  // Dynamic Specs management
  const addSpecField = () => setFormSpecs([...formSpecs, { key: '', value: '' }]);
  const removeSpecField = (idx: number) => setFormSpecs(formSpecs.filter((_, i) => i !== idx));
  const updateSpecField = (idx: number, field: 'key' | 'value', val: string) => {
    const updated = [...formSpecs];
    updated[idx][field] = val;
    setFormSpecs(updated);
  };

  // Upload product image directly to Google Drive Management Vault
  const handleProductImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('error', 'Fail Tidak Sah', 'Sila pilih fail imej (PNG, JPG, JPEG, WEBP).');
      return;
    }

    setIsUploadingPhoto(true);
    showToast('info', 'Memuat Naik Gambar', 'Menyimpan fail ke Cloudinary (folder asmm/merchandise)...');

    try {
      const res = await uploadProductImageToDrive(file, editingItem?.id, formName);
      setFormPhotoUrl(res.dataUrl);

      // Record item into drive files table
      await saveDriveFileDB(res.driveItem).catch(() => { });

      showToast(
        'success',
        'Gambar Disimpan ke Cloudinary',
        `Gambar produk berjaya dimuat naik ke Cloudinary (folder asmm/merchandise)!`
      );
    } catch (err: any) {
      console.error('Cloudinary upload error:', err);
      showToast('error', 'Ralat Muat Naik', 'Gagal memuat naik gambar produk ke Cloudinary.');
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // CRUD: Save product
  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canModifyInventory) return;

    if (!formName.trim() || formPrice <= 0) {
      showToast('error', 'Ralat Borang', 'Sila isi nama produk dan harga jualan yang sah.');
      return;
    }

    const sizesArr = formSizes.split(',').map(s => s.trim()).filter(Boolean);
    const variationsArr = formVariations.split(',').map(v => v.trim()).filter(Boolean);

    // Parse specs list into Record<string, string>
    const specsObj: Record<string, string> = {};
    formSpecs.forEach(spec => {
      if (spec.key.trim() && spec.value.trim()) {
        specsObj[spec.key.trim()] = spec.value.trim();
      }
    });

    const payload: MerchandiseItem = {
      id: editingItem ? editingItem.id : `merch-${Date.now()}`,
      name: formName.trim(),
      category: formCategory,
      price: formPrice,
      description: formDescription.trim(),
      photoUrl: formPhotoUrl.trim() || '/logo-pssasmm.png',
      sizes: sizesArr.length > 0 ? sizesArr : undefined,
      stockQuantity: typeof formStockQuantity === 'number' ? formStockQuantity : 0,
      stockStatus: formStockStatus,
      variations: variationsArr.length > 0 ? variationsArr : undefined,
      specifications: Object.keys(specsObj).length > 0 ? specsObj : undefined,
      userId: editingItem?.userId || currentUser?.id || 'ASMM-011'
    };

    try {
      await saveItem(payload);
      showToast('success', editingItem ? 'Kemaskini Berjaya' : 'Pendaftaran Berjaya', `Barangan "${formName}" telah direkodkan.`);
      setIsAddEditModalOpen(false);
    } catch (err) {
      console.error('handleSaveProduct error:', err);
      showToast('error', 'Ralat Sistem', 'Gagal merekodkan barangan jualan.');
    }
  };

  // Superadmin: Delete Cancelled Order
  const handleConfirmDeleteOrder = async () => {
    if (!orderToDelete) return;
    if (!isSuperAdmin(currentUser)) {
      showToast('error', 'Akses Dihalang', 'Hanya Superadmin sahaja dibenarkan memadam rekod tempahan yang dibatalkan.');
      return;
    }

    setIsDeletingOrder(true);
    try {
      await deleteOrder(orderToDelete.id);
      showToast('success', 'Tempahan Dipadam', `Rekod tempahan batal #${orderToDelete.id.slice(-6).toUpperCase()} berjaya dipadam secara kekal.`);
      setOrderToDelete(null);
    } catch {
      showToast('error', 'Ralat', 'Gagal memadam rekod tempahan.');
    } finally {
      setIsDeletingOrder(false);
    }
  };

  // CRUD: Delete Product (Custom modal without browser window.confirm)
  const handleDeleteProduct = (productId: string, productName: string) => {
    if (!canModifyInventory) return;
    setProductToDelete({ id: productId, name: productName });
  };

  const handleConfirmDeleteProduct = async () => {
    if (!productToDelete) return;
    setIsDeletingProduct(true);
    try {
      await deleteItem(productToDelete.id);
      showToast('success', 'Barangan Dipadam', `Barangan "${productToDelete.name}" telah dibuang dari inventori.`);
      setProductToDelete(null);
    } catch {
      showToast('error', 'Ralat', 'Gagal memadam barangan.');
    } finally {
      setIsDeletingProduct(false);
    }
  };

  // Access check
  if (!currentUser || !isManagement) {
    return (
      <div className="w-full space-y-6 min-h-[75vh]">
        <PageHeader badgeText="Inventori Kedai" badgeIcon={Package} title="Inventori & Pengurusan Tempahan" />
        <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center space-y-3">
          <ShieldAlert className="w-12 h-12 text-rose-400 mx-auto" />
          <h4 className="font-extrabold text-sm text-slate-700">Akses Terhad</h4>
          <p className="text-xs text-slate-400 font-medium">Halaman ini hanya untuk pihak pengurusan persatuan (Pengerusi, Setiausaha, Bendahari, Ketua Gelanggang, AJK).</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 min-h-[75vh]">
      {/* Page Header */}
      <PageHeader
        badgeText="Pengurusan Kedai"
        badgeIcon={Package}
        title="Inventori & Tempahan"
        titleBadge={overallStats.totalOrders > 0 ? `${overallStats.totalOrders} tempahan` : undefined}
      />

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Jumlah Tempahan', value: overallStats.totalOrders, color: 'text-slate-800', bg: 'bg-white border-slate-200', icon: ClipboardList },
          { label: 'Menunggu', value: overallStats.pendingOrders, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', icon: Clock },
          { label: 'Disediakan', value: overallStats.preparingOrders, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', icon: Package },
          { label: 'Selesai', value: overallStats.completedOrders, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle },
          { label: 'Hasil Jualan', value: `RM ${overallStats.totalRevenue.toFixed(2)}`, color: 'text-asmm-red', bg: 'bg-red-50 border-red-200', icon: CreditCard },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className={`${stat.bg} border rounded-2xl p-3 sm:p-4 space-y-1`}>
              <div className="flex items-center gap-1.5">
                <Icon className={`w-3.5 h-3.5 ${stat.color}`} />
                <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">{stat.label}</span>
              </div>
              <div className={`text-base sm:text-lg font-extrabold ${stat.color}`}>{stat.value}</div>
            </div>
          );
        })}
      </div>

      {/* View Tabs */}
      <div className="flex border-b border-slate-200 gap-1">
        <button
          onClick={() => setActiveView('orders')}
          className={`px-5 py-3 text-xs font-extrabold border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${activeView === 'orders'
              ? 'border-asmm-red text-asmm-red'
              : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
        >
          <ClipboardList className="w-4 h-4" />
          <span>Urus Tempahan</span>
        </button>
        <button
          onClick={() => setActiveView('inventory')}
          className={`px-5 py-3 text-xs font-extrabold border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${activeView === 'inventory'
              ? 'border-asmm-red text-asmm-red'
              : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
        >
          <BarChart3 className="w-4 h-4" />
          <span>Stok Barangan</span>
        </button>
      </div>

      {/* === ORDER MANAGEMENT VIEW === */}
      {activeView === 'orders' && (
        <div className="space-y-4">
          {/* Filter Bar */}
          <div className="flex flex-col sm:flex-row items-center gap-3 bg-white border border-slate-200 rounded-2xl p-3 shadow-xs">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cari nama ahli atau barangan..."
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-bold text-slate-900"
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar flex-nowrap w-full sm:w-auto pb-0.5">
              <Filter className="w-4 h-4 text-slate-400 shrink-0" />
              {[
                { id: 'all', label: 'Semua', count: baseManagedOrders.length },
                { id: 'Menunggu Kelulusan', label: 'Menunggu', count: baseManagedOrders.filter(o => o.status === 'Menunggu Kelulusan').length },
                { id: 'Sedang Disediakan', label: 'Diproses', count: baseManagedOrders.filter(o => o.status === 'Sedang Disediakan').length },
                { id: 'Selesai', label: 'Selesai', count: baseManagedOrders.filter(o => o.status === 'Selesai').length },
                { id: 'Dibatalkan', label: 'Batal', count: baseManagedOrders.filter(o => o.status === 'Dibatalkan').length },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setStatusFilter(f.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap shrink-0 border transition-all cursor-pointer flex items-center gap-1.5 ${statusFilter === f.id
                      ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                      : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-slate-200'
                    }`}
                >
                  {f.label}
                  {f.count > 0 && (
                    <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-full ${statusFilter === f.id ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500'
                      }`}>{f.count}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center">
              <div className="w-8 h-8 border-3 border-asmm-red border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs text-slate-400 font-medium mt-3">Memuatkan tempahan...</p>
            </div>
          ) : managedOrders.length > 0 ? (
            <div className="space-y-3">
              {managedOrders.map(o => {
                const invoiceStatus = getInvoicePaymentStatus(o.invoiceId);
                const isPaid = o.paymentStatus === 'Lulus / Dibayar' || invoiceStatus === 'Lulus / Dibayar';
                const hasProof = Boolean(o.paymentProofUrl);
                const isCancelled = o.status === 'Dibatalkan';

                const statusSteps = [
                  { key: 'Menunggu Kelulusan', label: 'Menunggu', icon: Clock, color: 'amber' },
                  { key: 'Sedang Disediakan', label: 'Diproses', icon: Package, color: 'blue' },
                  { key: 'Selesai', label: 'Selesai', icon: CheckCircle, color: 'emerald' },
                ] as const;
                const currentStepIndex = statusSteps.findIndex(s => s.key === o.status);

                return (
                  <div key={o.id} className={`bg-white border rounded-2xl shadow-xs hover:shadow-sm transition-all overflow-hidden ${isCancelled ? 'border-slate-200 opacity-60' : 'border-slate-200'
                    }`}>
                    <div className={`h-1 w-full ${isCancelled ? 'bg-slate-300' :
                        o.status === 'Selesai' ? 'bg-emerald-500' :
                          o.status === 'Sedang Disediakan' ? 'bg-blue-500' :
                            'bg-amber-500'
                      }`} />

                    <div className="p-4 sm:p-5">
                      {/* Header: Member + Amount */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-extrabold shrink-0 ${isCancelled ? 'bg-slate-100 text-slate-400' :
                              o.status === 'Selesai' ? 'bg-emerald-100 text-emerald-700' :
                                o.status === 'Sedang Disediakan' ? 'bg-blue-100 text-blue-700' :
                                  'bg-amber-100 text-amber-700'
                            }`}>
                            {o.memberName.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-extrabold text-sm text-slate-900 truncate">{o.memberName}</h4>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className="text-[10px] font-bold text-slate-400">{o.gelanggang}</span>
                              <span className="text-[10px] text-slate-300">•</span>
                              <span className="text-[10px] font-bold text-slate-400 font-mono">#{o.id.slice(-6)}</span>
                              <span className="text-[10px] text-slate-300">•</span>
                              <span className="text-[10px] font-medium text-slate-400">{o.createdAt}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-base font-extrabold text-asmm-red">RM {o.totalAmount.toFixed(2)}</div>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[9px] font-bold border mt-1 ${isPaid
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : hasProof
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : 'bg-rose-50 text-rose-700 border-rose-200'
                            }`}>
                            <CreditCard className="w-2.5 h-2.5" />
                            {isPaid ? 'Dibayar (QR DuitNow)' : hasProof ? 'Slip Dihantar' : 'Belum Bayar'}
                          </span>
                        </div>
                      </div>

                      {/* Item Details */}
                      <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-100 rounded-xl mb-3">
                        <ShoppingBag className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="font-bold text-xs text-slate-800">{o.itemName}</span>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {o.size && <span className="text-[10px] font-medium text-slate-500">Saiz: <strong>{o.size}</strong></span>}
                            <span className="text-[10px] font-medium text-slate-500">Qty: <strong>{o.quantity}</strong></span>
                            <span className="text-[10px] text-slate-300">|</span>
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500">
                              <Truck className="w-3 h-3" /> {o.deliveryMethod}
                            </span>
                            {o.pickupLocation && (
                              <span className="text-[10px] text-slate-500">📍 {o.pickupLocation}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Notes */}
                      {o.notes && (
                        <div className="flex items-start gap-2 p-2.5 bg-amber-50/50 border border-amber-100 rounded-xl mb-3">
                          <MessageSquare className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                          <p className="text-[10px] font-medium text-amber-800 leading-relaxed italic">{o.notes}</p>
                        </div>
                      )}

                      {/* QR Payment Verification & Delivery Control */}
                      <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-xl mb-3 space-y-2.5">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-red-100 text-asmm-red flex items-center justify-center font-bold shrink-0">
                              <QrCode className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="text-[11px] font-extrabold text-slate-800">
                                Kaedah: {o.paymentMethod || 'DuitNow QR'}
                              </div>
                              {o.paymentRef && (
                                <div className="text-[10px] text-slate-500 font-mono">
                                  Ref: <strong className="text-slate-700">{o.paymentRef}</strong>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-wrap">
                            {o.paymentProofUrl && (
                              <button
                                type="button"
                                onClick={() => setViewingProofUrl(o.paymentProofUrl!)}
                                className="px-2.5 py-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-lg text-[10px] font-extrabold flex items-center gap-1.5 shadow-xs cursor-pointer hover:bg-slate-50 transition-all"
                              >
                                <Eye className="w-3.5 h-3.5 text-blue-600" />
                                <span>Lihat Slip Bayaran</span>
                              </button>
                            )}

                            {!isPaid && (
                              <button
                                type="button"
                                onClick={() => handleVerifyQrPayment(o)}
                                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[10px] font-extrabold flex items-center gap-1.5 shadow-xs cursor-pointer transition-all"
                              >
                                <ShieldCheck className="w-3.5 h-3.5" />
                                <span>Sahkan Bayaran QR (Luluskan)</span>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Delivery & Postal Tracking Management */}
                        {o.deliveryMethod === 'Penghantaran Pos' && (
                          <div className="pt-2 border-t border-slate-200/60 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600">
                              <Truck className="w-3.5 h-3.5 text-slate-400" />
                              <span>No. Penjejakan Pos:</span>
                              {o.trackingNo ? (
                                <span className="font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                  {o.trackingNo}
                                </span>
                              ) : (
                                <span className="text-amber-600 font-normal italic">Belum dimasukkan</span>
                              )}
                            </div>

                            <div className="flex items-center gap-1.5 w-full sm:w-auto">
                              <input
                                type="text"
                                placeholder="Cth: JNT123456789"
                                value={trackingInput[o.id] !== undefined ? trackingInput[o.id] : (o.trackingNo || '')}
                                onChange={(e) => setTrackingInput({ ...trackingInput, [o.id]: e.target.value })}
                                className="px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-mono text-slate-900 focus:outline-none focus:border-asmm-red w-full sm:w-40"
                              />
                              <button
                                type="button"
                                disabled={isSavingTracking === o.id}
                                onClick={() => handleSaveTracking(o.id)}
                                className="px-2.5 py-1 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-lg text-[10px] font-extrabold cursor-pointer whitespace-nowrap shadow-xs"
                              >
                                {isSavingTracking === o.id ? 'Menyimpan...' : 'Simpan Tracking'}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* WhatsApp Direct Buyer Link */}
                        {(() => {
                          const buyerPhone = o.phone || members.find(m => m.id === o.memberId)?.phone;
                          if (!buyerPhone) return null;
                          let cleanPhone = buyerPhone.replace(/[^0-9]/g, '');
                          if (cleanPhone.startsWith('0')) cleanPhone = '6' + cleanPhone;
                          else if (!cleanPhone.startsWith('60') && cleanPhone.startsWith('1')) cleanPhone = '60' + cleanPhone;
                          return (
                            <div className="pt-1.5 border-t border-slate-200/60 flex items-center justify-between text-[10px]">
                              <span className="text-slate-500 font-medium">Pembeli: <strong>{o.memberName}</strong> ({buyerPhone})</span>
                              <a
                                href={`https://wa.me/${cleanPhone}?text=${encodeURIComponent(
                                  `Salam saudara/saudari ${o.memberName}, pihak pengurusan ASMM menghubungi mengenai tempahan #${o.id.slice(-6)} bagi produk *${o.itemName}*.`
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-bold no-underline"
                              >
                                <MessageSquare className="w-3 h-3" />
                                <span>WhatsApp Pembeli</span>
                              </a>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Status Pipeline */}
                      {!isCancelled ? (
                        <div className="flex items-center gap-1 sm:gap-2">
                          {statusSteps.map((step, i) => {
                            const StepIcon = step.icon;
                            const isCompleted = i <= currentStepIndex;
                            const isCurrent = i === currentStepIndex;
                            const isNext = i === currentStepIndex + 1;

                            return (
                              <React.Fragment key={step.key}>
                                {i > 0 && (
                                  <div className={`flex-1 h-0.5 rounded-full transition-all ${i <= currentStepIndex ? 'bg-emerald-400' : 'bg-slate-200'
                                    }`} />
                                )}
                                <button
                                  onClick={() => {
                                    if (isNext) handleUpdateOrderStatus(o.id, step.key as MerchandiseOrder['status']);
                                  }}
                                  disabled={!isNext}
                                  className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all whitespace-nowrap ${isCurrent
                                      ? step.color === 'amber' ? 'bg-amber-100 text-amber-800 border border-amber-300 ring-2 ring-amber-200/50' :
                                        step.color === 'blue' ? 'bg-blue-100 text-blue-800 border border-blue-300 ring-2 ring-blue-200/50' :
                                          'bg-emerald-100 text-emerald-800 border border-emerald-300 ring-2 ring-emerald-200/50'
                                      : isCompleted
                                        ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                                        : isNext
                                          ? 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 cursor-pointer shadow-xs hover:shadow-sm'
                                          : 'bg-slate-50 text-slate-400 border border-slate-100'
                                    } ${isNext ? 'cursor-pointer' : 'cursor-default'}`}
                                >
                                  <StepIcon className={`w-3 h-3 ${isCurrent ? 'animate-pulse' : ''}`} />
                                  <span className="hidden sm:inline">{step.label}</span>
                                </button>
                              </React.Fragment>
                            );
                          })}

                          {o.status !== 'Selesai' && (
                            <>
                              <div className="flex-1" />
                              <button
                                onClick={() => handleUpdateOrderStatus(o.id, 'Dibatalkan')}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[10px] font-bold text-rose-500 hover:text-rose-700 bg-white hover:bg-rose-50 border border-slate-200 hover:border-rose-200 transition-all cursor-pointer"
                              >
                                <X className="w-3 h-3" />
                                <span className="hidden sm:inline">Batal</span>
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-3 p-3 bg-rose-50/70 border border-rose-200/80 rounded-2xl flex-wrap">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                              <X className="w-4 h-4" />
                            </div>
                            <div>
                              <span className="text-xs font-extrabold text-rose-950 block">Tempahan ini telah dibatalkan</span>
                              <span className="text-[10px] text-rose-700 font-medium">Status pesanan adalah 'Dibatalkan'.</span>
                            </div>
                          </div>

                          {isSuperAdmin(currentUser) && (
                            <button
                              type="button"
                              onClick={() => setOrderToDelete(o)}
                              className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-[11px] font-extrabold shadow-sm hover:shadow transition-all cursor-pointer active:scale-95"
                              title="Padam rekod tempahan batal ini secara kekal (Superadmin)"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Padam Rekod Batal</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center space-y-3">
              <ClipboardList className="w-12 h-12 text-slate-300 mx-auto" />
              <h4 className="font-extrabold text-sm text-slate-700">Tiada Tempahan Dijumpai</h4>
              <p className="text-xs text-slate-400 font-medium">
                {statusFilter !== 'all' || searchTerm
                  ? 'Cuba ubah penapis atau kata kunci carian.'
                  : 'Belum ada tempahan merchandise daripada ahli persatuan.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* === INVENTORY / STOCK VIEW === */}
      {activeView === 'inventory' && (
        <div className="space-y-4">
          {/* Header & Controls bar */}
          <div className="flex items-center justify-between gap-4 bg-white p-4 border border-slate-200 rounded-2xl shadow-xs">
            <div className="text-xs text-slate-500 font-bold">
              Senarai penuh barangan jualan rasmi kedai persatuan.
            </div>
            {canModifyInventory && (
              <button
                onClick={() => openAddEditModal()}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-extrabold rounded-xl transition-all shadow-md flex items-center gap-1.5 cursor-pointer shrink-0"
              >
                <Plus className="w-4 h-4" />
                <span>Tambah Produk</span>
              </button>
            )}
          </div>

          {inventoryStats.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {inventoryStats.map(item => (
                <div key={item.id} className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between group">
                  <div>
                    {/* Item Image */}
                    <div className="relative aspect-square bg-slate-100 overflow-hidden border-b border-slate-200">
                      <img
                        src={item.photoUrl}
                        alt={item.name}
                        onError={(e) => { (e.target as HTMLImageElement).src = '/logo-pssasmm.png'; }}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <span className="absolute top-3 left-3 px-2.5 py-1 bg-slate-900/90 text-white text-[9px] font-extrabold rounded-full tracking-wider uppercase">
                        {item.category}
                      </span>
                      <span className={`absolute top-3 right-3 px-2.5 py-1 text-[9px] font-extrabold rounded-full tracking-wider ${item.stockStatus === 'Dalam Stok' ? 'bg-emerald-500 text-white' :
                          item.stockStatus === 'Stok Rendah' ? 'bg-amber-500 text-white' :
                            'bg-rose-500 text-white'
                        }`}>
                        {item.stockStatus}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="p-5 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-extrabold text-sm sm:text-base text-slate-900 leading-tight">{item.name}</h4>
                        <span className="text-sm font-extrabold text-asmm-red whitespace-nowrap">
                          {item.id === 'merch-baju-silat' || item.name.toLowerCase().includes('baju silat')
                            ? 'RM 120.00 – RM 140.00'
                            : `RM ${item.price.toFixed(2)}`}
                        </span>
                      </div>

                      {/* Order & Inventory Stock Stats */}
                      <div className="grid grid-cols-3 gap-1.5 text-center">
                        <div className="bg-slate-50 border border-slate-100 rounded-xl p-2">
                          <div className="text-[9px] font-bold text-slate-400 uppercase">Stok Asal</div>
                          <div className="text-xs sm:text-sm font-extrabold text-slate-800 mt-0.5">
                            {item.stockQuantity !== undefined ? item.stockQuantity : '—'}
                          </div>
                        </div>
                        <div className="bg-slate-50 border border-slate-100 rounded-xl p-2">
                          <div className="text-[9px] font-bold text-slate-400 uppercase">Ditempah</div>
                          <div className="text-xs sm:text-sm font-extrabold text-slate-800 mt-0.5">
                            {item.totalOrdered}
                          </div>
                        </div>
                        <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-2">
                          <div className="text-[9px] font-bold text-emerald-600 uppercase">Baki Stok</div>
                          <div className={`text-xs sm:text-sm font-extrabold mt-0.5 ${item.stockQuantity !== undefined && (item.stockQuantity - item.totalOrdered) <= 5
                              ? 'text-rose-600'
                              : 'text-emerald-700'
                            }`}>
                            {item.stockQuantity !== undefined
                              ? Math.max(0, item.stockQuantity - item.totalOrdered)
                              : 'Fleksibel'}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">Hasil Jualan Bersih</span>
                        <span className="font-extrabold text-emerald-700">RM {item.revenue.toFixed(2)}</span>
                      </div>

                      {/* Status Breakdown */}
                      <div className="flex items-center gap-1.5 flex-wrap pt-1.5">
                        {item.pending > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-lg">
                            <Clock className="w-3 h-3" /> {item.pending} menunggu
                          </span>
                        )}
                        {item.preparing > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-lg">
                            <Package className="w-3 h-3" /> {item.preparing} disediakan
                          </span>
                        )}
                        {item.completed > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-lg">
                            <CheckCircle className="w-3 h-3" /> {item.completed} selesai
                          </span>
                        )}
                        {item.orderCount === 0 && (
                          <span className="text-[10px] font-medium text-slate-400 italic">Tiada tempahan lagi</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions footer */}
                  <div className="px-5 pb-5 pt-3 border-t border-slate-50 flex items-center justify-between gap-2 mt-auto">
                    <button
                      onClick={() => setSelectedItemForView(item)}
                      className="px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1"
                    >
                      <Info className="w-3.5 h-3.5" />
                      <span>Lihat Butiran</span>
                    </button>

                    {canModifyInventory && (
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => openAddEditModal(item)}
                          className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors cursor-pointer"
                          title="Kemaskini"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(item.id, item.name)}
                          className="p-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg transition-colors cursor-pointer"
                          title="Padam"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center space-y-3">
              <Package className="w-12 h-12 text-slate-300 mx-auto" />
              <h4 className="font-extrabold text-sm text-slate-700">Tiada Barangan Jualan</h4>
              <p className="text-xs text-slate-400 font-medium">Sila daftarkan barangan jualan rasmi persatuan di butang "Tambah Produk" di atas.</p>
            </div>
          )}
        </div>
      )}

      {/* === ADD / EDIT PRODUCT MODAL === */}
      {isAddEditModalOpen && (
        <Modal
          isOpen={isAddEditModalOpen}
          onClose={() => setIsAddEditModalOpen(false)}
          title={editingItem ? 'Kemaskini Butiran Produk' : 'Daftar Produk Baru'}
          maxWidth="max-w-2xl"
        >
          <form onSubmit={handleSaveProduct} className="space-y-4 font-sans text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Product Name */}
              <div className="sm:col-span-2">
                <label className="block text-slate-700 font-bold mb-1">Nama Produk *</label>
                <input
                  type="text"
                  required
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Contoh: Baju Silat Tradisional Awang Sulung"
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-slate-700 font-bold mb-1">Kategori Produk *</label>
                <select
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value as any)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                >
                  <option value="Pakaian">Pakaian</option>
                  <option value="Kelengkapan">Kelengkapan Silat</option>
                  <option value="Aksesori">Aksesori</option>
                </select>
              </div>

              {/* Price */}
              <div>
                <label className="block text-slate-700 font-bold mb-1">Harga Jualan (RM) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={formPrice || ''}
                  onChange={(e) => setFormPrice(Number(e.target.value))}
                  placeholder="Contoh: 75.00"
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                />
              </div>

              {/* Photo URL & Cloudinary Upload */}
              <div className="sm:col-span-2 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-slate-700 font-bold">
                    URL Gambar Produk
                  </label>
                  <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Cloudinary Vault (asmm/merchandise)
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row gap-2.5 items-start sm:items-center">
                  {/* Photo Preview Thumbnail */}
                  <div className="w-11 h-11 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden shrink-0 flex items-center justify-center">
                    {formPhotoUrl ? (
                      <img
                        src={formPhotoUrl}
                        alt="Pratonton"
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).src = '/logo-pssasmm.png'; }}
                      />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-slate-300" />
                    )}
                  </div>

                  <div className="flex-1 w-full flex gap-2">
                    <input
                      type="text"
                      value={formPhotoUrl}
                      onChange={(e) => setFormPhotoUrl(e.target.value)}
                      placeholder="Contoh: https://res.cloudinary.com/... atau baju-silat.jpg"
                      className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-mono text-slate-800 focus:outline-none focus:border-asmm-red"
                    />

                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleProductImageUpload}
                      className="hidden"
                    />

                    <button
                      type="button"
                      disabled={isUploadingPhoto}
                      onClick={() => fileInputRef.current?.click()}
                      className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shrink-0 cursor-pointer ${isUploadingPhoto
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                          : 'bg-slate-900 hover:bg-slate-800 text-white shadow-xs'
                        }`}
                      title="Pilih gambar dari komputer dan muat naik terus ke Cloudinary (folder asmm/merchandise)"
                    >
                      <Upload className={`w-3.5 h-3.5 ${isUploadingPhoto ? 'animate-bounce' : ''}`} />
                      <span>{isUploadingPhoto ? 'Memuat Naik...' : 'Muat Naik Gambar'}</span>
                    </button>
                  </div>
                </div>
                <div className="text-[10px] text-slate-400 flex items-center justify-between">
                  <span>Fail imej dimampatkan dan disimpan secara kekal ke Cloudinary CDN (<span className="font-mono text-slate-600">folder: asmm/merchandise</span>).</span>
                </div>
              </div>

              {/* If Baju Silat / Non-Bengkung: Variasi field is above Pilihan Saiz */}
              {!(formName.toLowerCase().includes('bengkung') || formCategory === 'Kelengkapan') && (
                <div className="sm:col-span-2">
                  <label className="block text-slate-700 font-bold mb-1">Variasi Produk (Asingkan dengan koma)</label>
                  <input
                    type="text"
                    value={formVariations}
                    onChange={(e) => setFormVariations(e.target.value)}
                    placeholder="Contoh: Hitam Sulaman Standard, Hitam Sulaman Khas Emas"
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                  />
                </div>
              )}

              {/* Sizes / Colors */}
              <div>
                <label className="block text-slate-700 font-bold mb-1">
                  {formName.toLowerCase().includes('bengkung') || formCategory === 'Kelengkapan'
                    ? 'Pilihan Warna (Asingkan dengan koma)'
                    : 'Pilihan Saiz (Asingkan dengan koma)'}
                </label>
                <input
                  type="text"
                  value={formSizes}
                  onChange={(e) => setFormSizes(e.target.value)}
                  placeholder={
                    formName.toLowerCase().includes('bengkung') || formCategory === 'Kelengkapan'
                      ? "Contoh: Hitam, Putih, Merah, Kuning"
                      : "Contoh: S, M, L, XL, XXL"
                  }
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                />
              </div>

              {/* Stock Quantity */}
              <div>
                <label className="block text-slate-700 font-bold mb-1">Kuantiti Stok Masuk (Unit) *</label>
                <input
                  type="number"
                  min="0"
                  required
                  value={formStockQuantity}
                  onChange={(e) => setFormStockQuantity(Math.max(0, parseInt(e.target.value) || 0))}
                  placeholder="Contoh: 50"
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                />
              </div>

              {/* If Bengkung: Variasi field is below Warna */}
              {(formName.toLowerCase().includes('bengkung') || formCategory === 'Kelengkapan') && (
                <div className="sm:col-span-2">
                  <label className="block text-slate-700 font-bold mb-1">Variasi Produk (Asingkan dengan koma)</label>
                  <input
                    type="text"
                    value={formVariations}
                    onChange={(e) => setFormVariations(e.target.value)}
                    placeholder="Contoh: Kosong, Cula 1, Cula 2, Cula 3, Cula 4"
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                  />
                </div>
              )}

              {/* Stock Status */}
              <div className="sm:col-span-2">
                <label className="block text-slate-700 font-bold mb-1">Status Ketersediaan Stok *</label>
                <select
                  value={formStockStatus}
                  onChange={(e) => setFormStockStatus(e.target.value as any)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                >
                  <option value="Dalam Stok">Dalam Stok</option>
                  <option value="Stok Rendah">Stok Rendah</option>
                  <option value="Tiada Stok">Tiada Stok</option>
                </select>
              </div>

              {/* Description */}
              <div className="sm:col-span-2">
                <label className="block text-slate-700 font-bold mb-1">Deskripsi Produk</label>
                <textarea
                  rows={3}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Butiran lengkap produk, saiz bengkung, bahan kain, ketahanan dsb."
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-asmm-red"
                />
              </div>

              {/* Specifications (Shopee specs style CRUD) */}
              <div className="sm:col-span-2 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-slate-700 font-bold">Spesifikasi Teknikal (Shopee Style Specs)</label>
                  <button
                    type="button"
                    onClick={addSpecField}
                    className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-extrabold cursor-pointer"
                  >
                    + Tambah Spesifikasi
                  </button>
                </div>
                <div className="space-y-2 bg-slate-50 p-3 rounded-2xl border border-slate-150">
                  {formSpecs.map((spec, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input
                        type="text"
                        placeholder="Tajuk (e.g. Bahan)"
                        value={spec.key}
                        onChange={(e) => updateSpecField(idx, 'key', e.target.value)}
                        className="flex-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                      />
                      <input
                        type="text"
                        placeholder="Nilai (e.g. Kapas 100%)"
                        value={spec.value}
                        onChange={(e) => updateSpecField(idx, 'value', e.target.value)}
                        className="flex-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold"
                      />
                      {formSpecs.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSpecField(idx)}
                          className="p-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg cursor-pointer"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions footer */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsAddEditModalOpen(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-750 font-bold rounded-xl transition-colors cursor-pointer"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-extrabold rounded-xl transition-all shadow-md cursor-pointer"
              >
                Simpan Barangan
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* === SINGLE PRODUCT DETAIL VIEW MODAL (SHOPEE STYLE FLOW) === */}
      {selectedItemForView && (
        <Modal
          isOpen={!!selectedItemForView}
          onClose={() => setSelectedItemForView(null)}
          title="Butiran Jualan Produk"
          maxWidth="max-w-2xl"
        >
          <div className="space-y-6 font-sans text-xs">
            {/* Shopee-style Product Flow Section */}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-5">
              {/* Image Box */}
              <div className="sm:col-span-5 aspect-square bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden shadow-inner shrink-0">
                <img
                  src={selectedItemForView.photoUrl}
                  alt={selectedItemForView.name}
                  onError={(e) => { (e.target as HTMLImageElement).src = '/logo-pssasmm.png'; }}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Title, Category, Pricing */}
              <div className="sm:col-span-7 space-y-4">
                <div>
                  <span className="text-[9px] font-extrabold px-2.5 py-0.5 rounded-full text-asmm-red bg-red-50 border border-red-100 uppercase tracking-wider">
                    {selectedItemForView.category}
                  </span>
                  <h3 className="font-extrabold text-base sm:text-lg text-slate-950 mt-1.5 leading-snug">
                    {selectedItemForView.name}
                  </h3>
                  <div className="text-xl font-extrabold text-asmm-red mt-1">
                    {selectedItemForView.id === 'merch-baju-silat' || selectedItemForView.name.toLowerCase().includes('baju silat')
                      ? 'RM 120.00 – RM 140.00'
                      : `RM ${selectedItemForView.price.toFixed(2)}`}
                  </div>
                </div>

                <div className="flex gap-2 items-center text-[10px] text-slate-500 font-bold bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                  <div className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${selectedItemForView.stockStatus === 'Dalam Stok' ? 'bg-emerald-500' : selectedItemForView.stockStatus === 'Stok Rendah' ? 'bg-amber-500' : 'bg-rose-500'
                      }`} />
                    <span>{selectedItemForView.stockStatus}</span>
                  </div>
                  <span className="text-slate-300">|</span>
                  <div>ID: {selectedItemForView.id}</div>
                </div>

                {/* Sizing flow */}
                {selectedItemForView.sizes && selectedItemForView.sizes.length > 0 && (
                  <div>
                    <label className="block text-slate-400 font-bold mb-1">
                      {selectedItemForView.name.toLowerCase().includes('bengkung') || selectedItemForView.category === 'Kelengkapan'
                        ? 'Pilihan Warna Tersedia'
                        : 'Variasi Saiz Tersedia'}
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedItemForView.sizes.map(s => (
                        <span key={s} className="px-2.5 py-1 bg-slate-100 border border-slate-200 text-slate-800 rounded-lg font-bold">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Variations list */}
                {selectedItemForView.variations && selectedItemForView.variations.length > 0 && (
                  <div>
                    <label className="block text-slate-400 font-bold mb-1">Pilihan Variasi Tersedia</label>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedItemForView.variations.map(v => (
                        <span key={v} className="px-2.5 py-1 bg-slate-100 border border-slate-200 text-slate-800 rounded-lg font-bold">
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Description & Technical Specs */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <div className="space-y-1.5">
                <h4 className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Penerangan Produk</h4>
                <p className="text-slate-700 leading-relaxed font-medium text-xs whitespace-pre-line bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                  {selectedItemForView.description || 'Tiada penerangan khusus disediakan untuk barangan ini.'}
                </p>
              </div>

              {/* Technical Specifications list */}
              {selectedItemForView.specifications && Object.keys(selectedItemForView.specifications).length > 0 && (
                <div className="space-y-1.5">
                  <h4 className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Spesifikasi Teknikal</h4>
                  <table className="w-full border-collapse border border-slate-150 rounded-xl overflow-hidden text-left">
                    <tbody>
                      {Object.entries(selectedItemForView.specifications).map(([key, val]) => (
                        <tr key={key} className="border-b border-slate-150">
                          <td className="p-2.5 bg-slate-50 font-bold text-slate-450 w-1/3">{key}</td>
                          <td className="p-2.5 font-bold text-slate-850">{val}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Seller Shop Profile Section (Shopee layout flow) */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-slate-50 border border-slate-200 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-asmm-red text-white flex items-center justify-center font-extrabold text-xs shadow-xs shrink-0 uppercase">
                  {sellerForView?.name ? formatSellerName(sellerForView.name).slice(0, 2) : 'AS'}
                </div>
                <div>
                  <h4 className="font-extrabold text-slate-800 text-[11px] sm:text-xs leading-none">{formatSellerName(sellerForView?.name)}</h4>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[10px] text-slate-500 font-bold">
                    <span className="text-asmm-red">{sellerForView?.role || 'Pengurus Kedah'}</span>
                    <span className="text-slate-355">•</span>
                    <span>📍 {sellerForView?.gelanggang === 'Semua Gelanggang' ? 'ASMM' : (sellerForView?.gelanggang || 'ASMM')}</span>
                  </div>
                </div>
              </div>
              {sellerForView?.phone && (() => {
                let cleanPhone = sellerForView.phone.replace(/[^0-9]/g, '');
                if (cleanPhone.startsWith('0')) {
                  cleanPhone = '6' + cleanPhone;
                } else if (!cleanPhone.startsWith('60') && cleanPhone.startsWith('1')) {
                  cleanPhone = '60' + cleanPhone;
                }
                return (
                  <a
                    href={`https://wa.me/${cleanPhone}?text=${encodeURIComponent(
                      `Salam sejahtera, saya berminat untuk bertanya mengenai produk *${selectedItemForView.name}* di portal ASMM:\n${window.location.origin}/merchandise/${selectedItemForView.id}`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-extrabold rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer no-underline shrink-0"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    Hubungi Whatsapp
                  </a>
                );
              })()}
            </div>

            {/* Seller Info Summary */}
            {(() => {
              const stats = inventoryStats.find(i => i.id === selectedItemForView.id);
              if (!stats) return null;
              return (
                <div className="grid grid-cols-3 gap-2 bg-slate-900 text-white rounded-2xl p-3.5 text-center">
                  <div>
                    <div className="text-[9px] font-bold text-slate-400 uppercase">Jumlah Ditempah</div>
                    <div className="text-sm font-extrabold text-white mt-0.5">{stats.totalOrdered} unit</div>
                  </div>
                  <div>
                    <div className="text-[9px] font-bold text-slate-400 uppercase">Tempahan Selesai</div>
                    <div className="text-sm font-extrabold text-emerald-400 mt-0.5">{stats.completed} unit</div>
                  </div>
                  <div>
                    <div className="text-[9px] font-bold text-slate-400 uppercase">Jumlah Jualan (RM)</div>
                    <div className="text-sm font-extrabold text-amber-400 mt-0.5">RM {stats.revenue.toFixed(2)}</div>
                  </div>
                </div>
              );
            })()}

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
              {canModifyInventory && (
                <button
                  onClick={() => {
                    setSelectedItemForView(null);
                    openAddEditModal(selectedItemForView);
                  }}
                  className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1"
                >
                  <Edit className="w-3.5 h-3.5" />
                  <span>Kemaskini Produk</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setSelectedItemForView(null)}
                className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-extrabold rounded-xl transition-all shadow-md cursor-pointer"
              >
                Tutup
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* === SLIP / PROOF LIGHTBOX MODAL === */}
      {viewingProofUrl && (
        <Modal
          isOpen={!!viewingProofUrl}
          onClose={() => setViewingProofUrl(null)}
          title="Bukti Slip Bayaran DuitNow QR"
          maxWidth="max-w-lg"
        >
          <div className="p-4 space-y-4 font-sans text-xs">
            <div className="max-h-[70vh] overflow-auto rounded-xl border border-slate-200 bg-slate-50 p-2 flex items-center justify-center">
              <img
                src={viewingProofUrl}
                alt="Slip Bayaran DuitNow"
                className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-xs"
              />
            </div>
            <div className="flex items-center justify-between">
              <a
                href={viewingProofUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all no-underline"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>Buka Gambar Asal</span>
              </a>
              <button
                type="button"
                onClick={() => setViewingProofUrl(null)}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold cursor-pointer"
              >
                Tutup
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* === MODAL SAHKAN PADAM TEMPAHAN BATAL (SUPERADMIN) === */}
      {orderToDelete && (
        <Modal
          isOpen={!!orderToDelete}
          onClose={() => !isDeletingOrder && setOrderToDelete(null)}
          title="Sahkan Padam Tempahan Batal"
          maxWidth="max-w-md"
        >
          <div className="p-4 space-y-4 font-sans text-xs">
            <div className="flex items-start gap-3 p-3.5 bg-rose-50 border border-rose-200 rounded-2xl text-rose-900">
              <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <h4 className="font-extrabold text-sm text-rose-950">Padam Tempahan Secara Kekal?</h4>
                <p className="text-xs text-rose-800 leading-relaxed">
                  Adakah anda pasti ingin memadam rekod tempahan batal <strong>#{orderToDelete.id.slice(-6).toUpperCase()}</strong> ({orderToDelete.itemName}) untuk ahli <strong>{orderToDelete.memberName}</strong>?
                </p>
                <p className="text-[10px] text-rose-600 font-bold">
                  * Tindakan Superadmin ini kekal dan tidak boleh dikembalikan.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                type="button"
                disabled={isDeletingOrder}
                onClick={() => setOrderToDelete(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl cursor-pointer disabled:opacity-50"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={isDeletingOrder}
                onClick={handleConfirmDeleteOrder}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-extrabold rounded-xl shadow-md cursor-pointer flex items-center gap-1.5 disabled:opacity-50 active:scale-95"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{isDeletingOrder ? 'Memadam...' : 'Ya, Padam Tempahan'}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* === MODAL SAHKAN PADAM PRODUK INVENTORI === */}
      {productToDelete && (
        <Modal
          isOpen={!!productToDelete}
          onClose={() => !isDeletingProduct && setProductToDelete(null)}
          title="Sahkan Padam Barangan Inventori"
          maxWidth="max-w-md"
        >
          <div className="p-4 space-y-4 font-sans text-xs">
            <div className="flex items-start gap-3 p-3.5 bg-rose-50 border border-rose-200 rounded-2xl text-rose-900">
              <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <h4 className="font-extrabold text-sm text-rose-950">Padam Barangan Dari Kedai?</h4>
                <p className="text-xs text-rose-800 leading-relaxed">
                  Adakah anda pasti ingin memadam barangan <strong>"{productToDelete.name}"</strong> secara kekal dari katalog inventori kedai?
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                type="button"
                disabled={isDeletingProduct}
                onClick={() => setProductToDelete(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl cursor-pointer disabled:opacity-50"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={isDeletingProduct}
                onClick={handleConfirmDeleteProduct}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-extrabold rounded-xl shadow-md cursor-pointer flex items-center gap-1.5 disabled:opacity-50 active:scale-95"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{isDeletingProduct ? 'Memadam...' : 'Ya, Padam Barangan'}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

